// components/DocumentUploader.jsx

import React, { useRef, useState } from 'react';
import { ReactSVG } from 'react-svg';
import icons from '../../constants/icons';
import { ErrorText, Text } from '../SecondaryMarket';
import ViewItems from '../ViewItems';
import dynamicfunction from '../../utils/dynamicfunction';
import { fontSizes } from '../../constants/constants';
import { getContentKeyAccToLang, showToast } from '../../utils/utils';
import { useSelector } from 'react-redux';

const MAX_FILE_SIZE = 3 * 1024 * 1024;
const ACCEPTED_FILE_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png'];

const DocumentUploader = ({
    label,
    documentTypeId,
    fileName,
    required = false,
    onUpload,
    onDelete,
    onDownload,
    className = '',
    getContentKey,
    error,
    disabled = false
}) => {
    const { TextWeight, isArabic } = dynamicfunction();
    const dashboardContent = useSelector((state) => state.content?.dashboardContent);
    const getCommonContentKey = (key) => getContentKeyAccToLang(dashboardContent, key, isArabic);

    const fileInputRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);

    const handleFile = (file) => {
        if (disabled) return;
        if (!file) return;

        const extension = file.name.split('.').pop()?.toLowerCase();

        if (!ACCEPTED_FILE_EXTENSIONS.includes(extension)) {
            showToast({
                icon: 'error',
                title: getCommonContentKey('file_type_error'),
            });
            return;
        }

        if (file.size > MAX_FILE_SIZE) {
            showToast({
                icon: 'error',
                title: getCommonContentKey('file_size_3mb_error'),
            });
            return;
        }

        onUpload?.({
            file,
            documentTypeId,
        });
    };

    return (
        <>
            <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => handleFile(e.target.files?.[0])}
            />

            <ViewItems className={className}>
                <ViewItems className="w-full flex !flex-row justify-between mb-2">
                    <Text
                        text={label}
                        size={fontSizes.h14}
                        className="text-primary-light"
                        isRequired={required}
                    />
                    <div className='flex items-center gap-2'>
                    {fileName && onDownload && (
                            <ReactSVG 
                            src={icons?.download}
                            className="cursor-pointer"
                            onClick={onDownload}
                            />
                        )}
                    {fileName && onDelete && (
                        <ReactSVG
                            src={icons.cross}
                            className="cursor-pointer"
                            onClick={onDelete}
                        />
                    )}

                    </div>
                </ViewItems>

                <div
                    className={`border border-dashed w-full p-5 rounded-xl flex flex-col items-center gap-1 bg-white ${
                        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                    } ${
                        isDragging ? 'border-primary' : ''
                    }`}
                    onClick={() => !disabled && fileInputRef.current?.click()}
                    onDragOver={(e) => {
                        e.preventDefault();
                        if (!disabled) setIsDragging(true);
                    }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={(e) => {
                        e.preventDefault();
                        setIsDragging(false);

                        if (disabled) return;

                        const file = e.dataTransfer.files?.[0];

                        if (file) {
                            handleFile(file);
                        }
                    }}
                >
                    <ReactSVG src={icons.upload_icon} className="mb-4" />

                    <Text
                        text={getContentKey("upload_drag_drop") || "Upload or drag and drop"}
                        weight={TextWeight.bold}
                        className="text-dark-blue"
                        size={fontSizes.h14}
                    />

                    <Text
                        text={fileName || getContentKey("image_or_pdf") || 'Image or pdf (max. 3 MB)'}
                        className="text-dark-grey/50"
                        size={fontSizes.h14}
                    />
                </div>
            </ViewItems>
            {error?.length > 0 && <ErrorText text={error} />}
        </>
    );
};

export default DocumentUploader;